import { useMutation, useQuery } from '@apollo/client';
import {
  FIND_ALL_PLAN_ACTIVATIONS_BY_PLAN,
  UPDATE_PLAN_ACTIVATION,
} from '../../../queries';

import { ColumnsType } from 'antd/es/table';
import { tableSortingMethods } from '../../../../../utils';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../../components/generic/Table/TableV3';
import { ExportColumn } from '../../../../../interfaces/ExportColumn';
import QueryErrorBoundary from '../../../../../components/error-handling';

export type FindAllPlanActivationByPlan = {
  level: string;
  criteria: string;
  uuid: string;
};

interface DataReturned {
  findAllPlanActivationByPlan: FindAllPlanActivationByPlan[];
}

interface ActivationCriteriaTableProps {
  planId: string;
}

export function ActivationCriteriaTable({
  planId,
}: ActivationCriteriaTableProps) {
  const { data, loading, error } = useQuery(FIND_ALL_PLAN_ACTIVATIONS_BY_PLAN, {
    variables: { uuid: planId },
    skip: !planId,
  });

  const [updateGQL] = useMutation(UPDATE_PLAN_ACTIVATION, {
    refetchQueries: [FIND_ALL_PLAN_ACTIVATIONS_BY_PLAN],
  });

  const fields = [
    {
      key: 'criteria',
      label: 'Criteria',
      type: 'textArea',
      required: true,
    },
  ];

  const columns: ColumnsType<any> = [
    {
      title: 'Level',
      key: 'level',
      width: 200,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.number(a.level, b.level),
      render: (_, { level }) => (
        <span className="custom-table--column custom-table--column-primary">
          Level {level}
        </span>
      ),
    },
    {
      title: 'Criteria',
      key: 'criteria',
      width: 1000,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.criteria, b.criteria),
      render: (_, { criteria }) => (
        <span className="custom-table--column">{criteria}</span>
      ),
    },
  ];

  const modifyVariables = ({ values, currentRecord }: ModifyVariablesArgs) => {
    return {
      ...values,
      level: currentRecord?.level,
    };
  };

  const baseColumns: ExportColumn[] = [
    {
      key: 'level',
      header: 'Level',
      type: 'text',
    },
    {
      key: 'criteria',
      header: 'Criteria',
      type: 'text',
      width: 400,
    },
  ];

  return (
    <QueryErrorBoundary error={error}>
      <TableV3
        title="Activation Criteria"
        columns={columns}
        data={data?.findAllPlanActivationByPlan || []}
        loading={loading}
        editable
        editableOptions={{
          create: false,
          delete: false,
          update: true,
        }}
        formFields={fields}
        modifyVariables={modifyVariables}
        updateGQL={updateGQL}
        customStyles="ant-table-wrapper--levels"
        exportExcelColumns={baseColumns}
      />
    </QueryErrorBoundary>
  );
}
